import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { type IntlShape } from 'react-intl-next';
import { z } from 'zod';

import {
  executeObjectResolverAction,
  type ExecuteActionRequest,
} from '@canvas/atlassian-service/datasource';

export interface ActionsServiceError {
  code: number;
  message: string;
}

interface JiraAssigneeEntity {
  atlassianUserId: string;
  displayName: string;
  avatarSource: string;
  url: string;
}

interface JiraAssigneeResponse {
  operationStatus: string;
  errors: ActionsServiceError[];
  entities: JiraAssigneeEntity[];
}

export const AssigneeUserSchema = z.object({
  accountId: z.string(),
  displayName: z.string(),
  avatarUrl: z.string(),
});

export type AssigneeUser = z.infer<typeof AssigneeUserSchema>;

export async function getAssignableUsers(params: {
  issueAri: string;
  query?: string;
  intl: IntlShape;
}): Promise<AssigneeUser[]> {
  try {
    const response = await executeObjectResolverAction({
      integrationKey: 'jira',
      actionKey: 'atlassian:work-item:get:assignees',
      parameters: {
        inputs: { query: params.query || '' },
        target: { ari: params.issueAri },
      },
    } as ExecuteActionRequest);

    const data = response as JiraAssigneeResponse;

    if (data.operationStatus !== 'SUCCESS') {
      throw new Error(`API returned ${data.operationStatus}`);
    }

    return data.entities.map((entity: JiraAssigneeEntity) => ({
      accountId: entity.atlassianUserId,
      displayName:
        entity.displayName ||
        params.intl.formatMessage(messages['jira-assignee.unknown-user.non-final']),
      avatarUrl: entity.avatarSource,
    }));
  } catch (error) {
    logError(
      'jira-assignee/get-assignable-users:failed',
      error instanceof Error ? error : new Error('Failed to fetch assignable users')
    );
    return [];
  }
}

export async function updateIssueAssignee(params: {
  assigneeAccountId: string;
  issueAri: string;
}): Promise<void> {
  try {
    await executeObjectResolverAction({
      integrationKey: 'jira',
      actionKey: 'atlassian:work-item:update:assignee',
      parameters: {
        inputs: {
          assignee: params.assigneeAccountId, // empty string to unassign
        },
        target: { ari: params.issueAri },
      },
    } as ExecuteActionRequest);
  } catch (error) {
    logError(
      'jira-assignee/update-assignee:failed',
      error instanceof Error ? error : new Error('Failed to update issue assignee')
    );
    throw new Error('Failed to update issue assignee');
  }
}
